
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { GitCompare } from 'lucide-react';
import { useCompanySearch } from '@/hooks/useCompanySearch';

const CompanyComparison = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get the pre-filled company from URL params
  const prefilledCompany = searchParams.get('company1') || '';
  
  const [company1, setCompany1] = useState(prefilledCompany);
  const [company2, setCompany2] = useState('');
  const [showDropdown1, setShowDropdown1] = useState(false);
  const [showDropdown2, setShowDropdown2] = useState(false);
  
  // Search hooks for both companies
  const {
    searchTerm: searchTerm1,
    setSearchTerm: setSearchTerm1,
    companyCards: companyCards1,
    handleSearch: handleSearch1
  } = useCompanySearch();

  const {
    searchTerm: searchTerm2,
    setSearchTerm: setSearchTerm2,
    companyCards: companyCards2,
    handleSearch: handleSearch2
  } = useCompanySearch();

  // Trigger search when user types in Company 1 field
  useEffect(() => {
    if (searchTerm1.length > 0 && !prefilledCompany) {
      handleSearch1();
      setShowDropdown1(true);
    } else {
      setShowDropdown1(false);
    }
  }, [searchTerm1, handleSearch1, prefilledCompany]);

  // Trigger search when user types in Company 2 field
  useEffect(() => {
    if (searchTerm2.length > 0) {
      handleSearch2();
      setShowDropdown2(true);
    } else {
      setShowDropdown2(false);
    }
  }, [searchTerm2, handleSearch2]);

  const handleCompany1Select = (companyName: string) => {
    setCompany1(companyName);
    setSearchTerm1('');
    setShowDropdown1(false);
  };

  const handleCompany2Select = (companyName: string) => {
    setCompany2(companyName);
    setSearchTerm2('');
    setShowDropdown2(false);
  };

  const handleCompare = () => {
    if (company1 && company2) {
      const encodedCompany1 = encodeURIComponent(company1);
      const encodedCompany2 = encodeURIComponent(company2);
      navigate(`/compare-results?company1=${encodedCompany1}&company2=${encodedCompany2}`);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-24 px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-redflags-dark mb-4">
              Compare Companies
            </h1>
            <p className="text-redflags-gray">
              Compare redundancy risk ratings and insights between two companies
            </p>
          </div>

          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="text-center">Select Companies to Compare</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-col md:flex-row items-center gap-4">
                {/* Company 1 */}
                <div className="flex-1 w-full relative">
                  <label className="block text-sm font-medium mb-2">Company 1</label>
                  {prefilledCompany ? (
                    <Input
                      value={company1}
                      onChange={(e) => setCompany1(e.target.value)}
                      placeholder="Enter first company name"
                      className="w-full"
                    />
                  ) : (
                    <>
                      <Input
                        value={company1 || searchTerm1}
                        onChange={(e) => {
                          if (company1) {
                            setCompany1('');
                          }
                          setSearchTerm1(e.target.value);
                        }}
                        placeholder="Search for first company"
                        className="w-full"
                      />
                      
                      {/* Dropdown for company 1 search results */}
                      {showDropdown1 && companyCards1.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {companyCards1.slice(0, 10).map((company) => (
                            <button
                              key={company.id}
                              onClick={() => handleCompany1Select(company.company)}
                              className="w-full text-left px-4 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-medium">{company.company}</div>
                              {company.location && (
                                <div className="text-sm text-gray-500">{company.location}</div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* VS separator */}
                <div className="text-xl font-bold text-redflags-red mt-6">VS</div>

                {/* Company 2 with search dropdown */}
                <div className="flex-1 w-full relative">
                  <label className="block text-sm font-medium mb-2">Company 2</label>
                  <Input
                    value={company2 || searchTerm2}
                    onChange={(e) => {
                      if (company2) {
                        setCompany2('');
                      }
                      setSearchTerm2(e.target.value);
                    }}
                    placeholder="Search for second company"
                    className="w-full"
                  />
                  
                  {/* Dropdown for company search results */}
                  {showDropdown2 && companyCards2.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {companyCards2.slice(0, 10).map((company) => (
                        <button
                          key={company.id}
                          onClick={() => handleCompany2Select(company.company)}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium">{company.company}</div>
                          {company.location && (
                            <div className="text-sm text-gray-500">{company.location}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Compare button */}
              <div className="text-center pt-4">
                <Button
                  onClick={handleCompare}
                  disabled={!company1 || !company2}
                  className="px-8 py-2 bg-redflags-red hover:bg-redflags-red/90"
                >
                  <GitCompare className="w-4 h-4 mr-2" />
                  Compare Companies
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CompanyComparison;
